import React from "react";
import { useTranslation } from "react-i18next";
import { Route, Switch, useParams, useRouteMatch } from "react-router-dom";
import SelfReadingDetail from "./SelfReadingDetail";
import ContractDetailSection from "../sections/ContractDetailSection";
import SelfReadingsSection from "../sections/SelfReadingsSection";
import { useContractDetailData } from "../hooks/useContractDetailData";
import NoContentWrapper from "features/shared/components/noContentWrapper/NoContentWrapper";
import CustomerState from "infrastructure/store/customer/CustomerState";
import IAppState from "infrastructure/store/IAppState";
import { useSelector } from "react-redux";
import ReservedCapacitySection from '../sections/ReservedCapacitySection';
import ContractDetailLayoutPage from './ContractDetailLayoutPage';
import { getContractDetailRootRoute } from "features/shared/helpers/routeHelper";
import CreateSelfReadingPage from 'features/contracts/pages/CreateSelfReadingPage';
import ConsumptionHistoryPage from 'features/contracts/pages/ConsumptionHistoryPage';
import RenderWrapper from "features/shared/components/renderWrapper/RenderWrapper";
import ReservedCapacityPage from "./ReservedCapacityPage";
import ChangeReservedCapacityGas from "./ChangeReservedCapacityGas";
import ChangeReservedCapacityElectricityMonthly from "./ChangeReservedCapacityElectricityMonthly";
import ChangeReservedCapacityElectricityYearly from "./ChangeReservedCapacityElectricityYearly";
import ContractDocumentsPage from "./ContractDocumentsPage";
import { isEnabledCustomForm } from "features/requirements/helpers/requirementTypeHelper";

export interface Props {
  id: string;
}

const ContractDetailMainPage = (props: Props) => {
  // translation
  const { t } = useTranslation(["shared\\moduleNames"]);

  // router
  let match = useRouteMatch();
  const { id: contractId } = useParams<Props>();

  // store
  const customer = (useSelector<IAppState, CustomerState>((store) => store.customer)).customer;

  // fetch data for contract
  const {
    contract: contractModel,
    contractEnergy,
    setContractEnergy,
    spotProduct,
    loadStatus,
  } = useContractDetailData(contractId, customer.customerId!);

  // destruct contract DTO from contract model
  const { contract } = contractModel;

  // render
  return (
    <NoContentWrapper data={contract} loadStatus={loadStatus}>
      <Switch>
        {/* DOCUMENTS */}
        <Route path={`${match.path}/documents`}>
          <ContractDocumentsPage contract={contract} contractEnergy={contractEnergy} />
        </Route>
        {/* CONSUMPTION-HISTORY */}
        <Route path={`${match.path}/consumption-history`}>
          <ConsumptionHistoryPage contract={contract} contractEnergy={contractEnergy} />
        </Route>
        {/* SELF-READINGS */}
        <Route exact path={`${match.path}/self-readings/create`}>
          <CreateSelfReadingPage contract={contract} contractEnergy={contractEnergy} />
        </Route>
        <Route path={`${match.path}/self-readings/:readingId`}>
          <RenderWrapper isVisible={() => isEnabledCustomForm("ZADANISAMOODECTU")}>
            <ContractDetailLayoutPage
              contract={contract}
              contractEnergy={contractEnergy}
              links={[
                { text: t("selfReadings.title"), to: getContractDetailRootRoute(contractId, "self-readings") },
                { text: t("selfReadingDetail.title") }
              ]}
            >
              <SelfReadingDetail contractType={contract.contractType!} />
            </ContractDetailLayoutPage>
          </RenderWrapper>
        </Route>
        <Route path={`${match.path}/self-readings`}>
          <RenderWrapper isVisible={() => isEnabledCustomForm("ZADANISAMOODECTU")}>
            <ContractDetailLayoutPage
              contract={contract}
              contractEnergy={contractEnergy}
              links={[{ text: t("selfReadings.title") }]}
            >
              <SelfReadingsSection
                contractId={contract.contractId!}
                contractEnergy={contractEnergy}
                contractStatus={contract.contractStatus!}
                contractType={contract.contractType!}
              />
            </ContractDetailLayoutPage>
          </RenderWrapper>
        </Route>
        {/* RESERVED-CAPACITY */}
        <Route exact path={`${match.path}/reserved-capacity/create/electricity/yearly`}>
          <ChangeReservedCapacityElectricityYearly contract={contract} contractEnergy={contractEnergy} />
        </Route>
        <Route exact path={`${match.path}/reserved-capacity/create/electricity/monthly`}>
          <ChangeReservedCapacityElectricityMonthly contract={contract} contractEnergy={contractEnergy} />
        </Route>
        <Route exact path={`${match.path}/reserved-capacity/create/gas`}>
          <ChangeReservedCapacityGas contract={contract} contractEnergy={contractEnergy} />
        </Route>
        <Route path={`${match.path}/reserved-capacity`}>
          <ReservedCapacityPage contract={contract} contractEnergy={contractEnergy} />
        </Route>
        {/* CONTRACT-DETAIL */}
        <Route path={`${match.path}/detail`}>
          <ContractDetailLayoutPage
            contract={contract}
            contractEnergy={contractEnergy}
            showLinkToDetail={false}
            links={[{ text: t("contractDetail.title") }]}
          >
            <ContractDetailSection
              contractModel={contractModel}
              contractEnergy={contractEnergy}
              setContractEnergy={setContractEnergy}
              spotProduct={spotProduct}
            />
          </ContractDetailLayoutPage>
        </Route>
      </Switch>
    </NoContentWrapper>
  );
};

export default ContractDetailMainPage;
